import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
} from "react";
import { ParcelDataReducer } from "../Reducers/ParcelDataReducer";
import { UserDataContext } from "./UserDataContext";
import { roles } from "../Mappings";
import { statuses } from "../Mappings";
import {
  getCurrentBranchesArray,
  getDateFromWeekAndDay,
} from "../GlobalFunctions";
import { MenuItem, InputAdornment } from "@material-ui/core";

export const ParcelDataContext = createContext();

const ParcelDataContextProvider = (props) => {
  const [parcelData, dispatch] = useReducer(ParcelDataReducer, {});
  const { userData } = useContext(UserDataContext);

  console.log("Rerender");

  const defaultParcel = {
    id: "",
    reason: "",
    approvalState: 0,
    userAdded: false,
    driverComments: "",
    adminComments: "",
    managerComments: "",

    cNoteDate: "1900-01-01T00:00:00",
    cNote: "",
    senderName: "",
    sendSuburb: "",
    receiverName: "",
    receiverSuburb: "",
    fPayer: "",
    puBranch: "",
    drBranch: "",
    delBranch: "",
    crBranch: "",
    truck: "",
    carrierName: "",
    domicile: "",
    items: 0,
    kg: 0,
    m3: 0,
    eventDate: "1900-01-01T00:00:00",
    mode: "",
    dpc: 0,
    receiverPOD: "",
    deliveryID: 0,
    shortDate: "1900-01-01T00:00:00",
    fMode: "",
  };

  const types = {
    P: "Pickup",
    D: "Delivery",
  };

  const comments = {
    [roles.tollAdmin]: "adminComments",
    [roles.ownerDriver]: "driverComments",
    [roles.tollManager]: "managerComments",
  }[userData.roleId];

  const getIsoString = (time, allValues) => {
    const { dateIsoString } = getDateFromWeekAndDay(
      allValues.week,
      allValues.day
    );
    return dateIsoString.replace("00:00:00", `${time}:00`);
  };

  const createParcelModel = useCallback((params) => {
    const currentBranchArray = getCurrentBranchesArray(
      userData.branches,
      params.week
    );
    const branchShortCodeObject = currentBranchArray.reduce((acc, cur) => {
      acc[cur.shortCode] = cur;
      return acc;
    }, {});

    return {
      eventTimeString: {
        displayName: "Time",
        displayValue: (value) => value,
        validate: (allValues) => allValues.eventTimeString !== "",
        mapToRequestValue: (allValues) => allValues.eventTimeString,
        textFieldProps: {
          name: "eventTimeString",
          type: "time",
          InputLabelProps: {
            shrink: true,
          },
        },
      },

      eventDate: {
        // displayName: "Time",
        // displayValue: (value) => value,
        // validate: (allValues) => true,
        mapToRequestValue: (allValues) =>
          getIsoString(allValues.eventTimeString, allValues),
        // textFieldProps: {
        //   name: "eventDate",
        //   type: "time",
        //   InputLabelProps: {
        //     shrink: true,
        //   },
        // },
      },

      cNote: {
        displayName: "Con-note Number",
        displayValue: (value) => value,
        validate: (allValues) => allValues.cNote !== "",
        mapToRequestValue: (allValues) => allValues.cNote,
        textFieldProps: {
          name: "cNote",
        },
      },

      senderName: {
        displayName: "Sender",
        displayValue: (value) => value,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.senderName,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "senderName",
        },
      },

      sendSuburb: {
        displayName: "Sender Suburb",
        displayValue: (value) => value,
        validate: (allValues) => /^[a-zA-Z]+$/.test(allValues.sendSuburb),
        mapToRequestValue: (allValues) => allValues.sendSuburb,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "sendSuburb",
        },
      },

      receiverName: {
        displayName: "Receiver",
        displayValue: (value) => value,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.receiverName,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "receiverName",
        },
      },

      receiverSuburb: {
        displayName: "Receiver Suburb",
        displayValue: (value) => value,
        validate: (allValues) => /^[a-zA-Z]+$/.test(allValues.receiverSuburb),
        mapToRequestValue: (allValues) => allValues.receiverSuburb,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "receiverSuburb",
        },
      },

      puBranch: {
        displayName: "Pickup Branch",
        displayValue: (value) => value,
        validate: (allValues) => allValues.puBranch !== "",
        mapToRequestValue: (allValues) => allValues.puBranch,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "puBranch",
          select: true,
          children: Object.keys(branchShortCodeObject).map((shortCode) => (
            <MenuItem key={shortCode} value={shortCode}>
              {branchShortCodeObject[shortCode].branchName}
            </MenuItem>
          )),
        },
      },

      delBranch: {
        displayName: "Delivery Branch",
        displayValue: (value) => value,
        validate: (allValues) => allValues.delBranch !== "",
        mapToRequestValue: (allValues) => allValues.delBranch,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "delBranch",
          select: true,
          children: Object.keys(branchShortCodeObject).map((shortCode) => (
            <MenuItem key={shortCode} value={shortCode}>
              {branchShortCodeObject[shortCode].branchName}
            </MenuItem>
          )),
        },
      },

      kg: {
        displayName: "Weight",
        displayValue: (value) => `${Math.round(value)}kg`,
        validate: (allValues) => /^\d+(\.\d{1,3})?$/.test(allValues.kg),
        mapToRequestValue: (allValues) => Number(allValues.kg),
        textFieldProps: {
          name: "kg",
          InputProps: { endAdornment: <InputAdornment>kg</InputAdornment> },
        },
      },

      m3: {
        displayName: "Volume",
        displayValue: (value) =>
          `${(Math.round(value * 1000) / 1000).toFixed(3)}m³`,
        validate: (allValues) => /^\d+(\.\d{1,3})?$/.test(allValues.m3),
        mapToRequestValue: (allValues) => Number(allValues.m3),
        textFieldProps: {
          name: "m3",
          InputProps: {
            endAdornment: (
              <InputAdornment>
                m
                <sup>
                  <sup>3</sup>
                </sup>
              </InputAdornment>
            ),
          },
        },
      },

      mode: {
        displayName: "Type",
        displayValue: (value) => types[value],
        validate: (allValues) => allValues.mode !== "",
        mapToRequestValue: (allValues) => allValues.mode,
        textFieldProps: {
          name: "mode",
          select: true,
          children: Object.keys(types).map((type) => (
            <MenuItem key={type} value={type}>
              {types[type]}
            </MenuItem>
          )),
        },
      },

      // dpc: {
      //   displayName: "DPC",
      //   displayValue: (value) => (Math.round(value * 100) / 100).toFixed(2),
      //   validate: (allValues) => /^\d+(\.\d{1,12})?$/.test(allValues.dpc),
      //   mapToRequestValue: (allValues) => Number(allValues.dpc),
      //   textFieldProps: {
      //     name: "dpc",
      //     InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
      //     select: true,
      //     children: ["0.00", "0.53", "0.71", "1.36"].map((e) => (
      //       <MenuItem key={e} value={e}>
      //         {e}
      //       </MenuItem>
      //     )),
      //   },
      // },

      dpc: {
        displayName: "DPC",
        displayValue: (value) =>
          `$${(Math.round(value * 100) / 100).toFixed(2)}`,
          validate: (allValues) => /^-?\d+(\.\d{1,12})?$/.test(allValues.dpc),
        mapToRequestValue: (allValues) => Number(allValues.dpc),
        textFieldProps: {
          name: "dpc",
          InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
        },
      },

      reason: {
        displayName: "Reason",
        displayValue: (value) => value,
        validate: (allValues) => allValues.reason !== "",
        mapToRequestValue: (allValues) => allValues.reason,
        textFieldProps: {
          name: "reason",
          placeholder: "Choose one",
          select: true,
        },
      },

      driverComments: {
        displayName: "Driver Comments",
        displayValue: (value) => value,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.driverComments,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "driverComments",
          placeholder: "Provide a brief description",
          multiline: true,
          variant: "outlined",
          rows: 3,
          rowsMax: 3,
        },
      },

      adminComments: {
        displayName: "Admin Comments",
        displayValue: (value) => value,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.adminComments,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "adminComments",
          placeholder: "Provide a brief description",
          multiline: true,
          variant: "outlined",
          rows: 3,
          rowsMax: 3,
        },
      },

      managerComments: {
        displayName: "Manager Comments",
        displayValue: (value) => value,
        validate: (allValues) => true,
        mapToRequestValue: (allValues) => allValues.managerComments,
        getValueFromRequest: (value) => value,
        textFieldProps: {
          name: "managerComments",
          placeholder: "Provide a brief description",
          multiline: true,
          variant: "outlined",
          rows: 3,
          rowsMax: 3,
        },
      },

      approvalState: {
        displayName: "Approval State",
        displayValue: (value, allValues) => statuses[value],
        mapToRequestValue: (allValues) => allValues.approvalState,
        getValueFromRequest: (value) => value,
      },
    };
  }, []);

  const displayColumns = [
    "eventTime",
    "cNote",
    "senderName",
    "sendSuburb",
    "receiverName",
    "receiverSuburb",
    "puBranch",
    "delBranch",
    "kg",
    "m3",
    "mode",
    "dpcParcel",
    "reason",
    comments,
  ];

  return (
    <ParcelDataContext.Provider
      value={{
        parcelData,
        dispatch,
        createParcelModel,
        comments,
        defaultParcel,
      }}
    >
      {props.children}
    </ParcelDataContext.Provider>
  );
};

export default ParcelDataContextProvider;
